import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { PieLabelRenderProps } from 'recharts';

export type StatusSliceName = 'Success' | 'Failure';

interface Props {
  successCount: number;
  failureCount: number;
  selected?: StatusSliceName | null;
  onSliceClick?: (name: StatusSliceName) => void;
}

interface Slice {
  name: StatusSliceName;
  value: number;
  color: string;
}

const renderSliceLabel = (props: PieLabelRenderProps): string => {
  const name = String(props.name ?? '');
  const value = Number(props.value ?? 0);
  const percent = Number(props.percent ?? 0);
  return `${name} (Rate ${(percent * 100).toFixed(2)}%) ${value}`;
};

export const PrintingStatusPieChart = ({
  successCount,
  failureCount,
  selected = null,
  onSliceClick,
}: Props) => {
  const data: Slice[] = (
    [
      { name: 'Success', value: successCount, color: '#4caf50' },
      { name: 'Failure', value: failureCount, color: '#f44336' },
    ] as Slice[]
  ).filter((d) => d.value > 0);

  if (data.length === 0) {
    return <div>No printings in range</div>;
  }

  const total = data.reduce((acc, d) => acc + d.value, 0);

  return (
    <Box position="relative" width="100%" height={300}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={60}
            outerRadius={90}
            dataKey="value"
            nameKey="name"
            label={renderSliceLabel}
            onClick={(entry: { name?: StatusSliceName }) => {
              if (onSliceClick && entry?.name) onSliceClick(entry.name);
            }}
            cursor={onSliceClick ? 'pointer' : 'default'}
          >
            {data.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.color}
                fillOpacity={selected && selected !== entry.name ? 0.35 : 1}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
      >
        <Typography variant="body2" fontWeight={500}>
          {`Total: ${total}`}
        </Typography>
      </Box>
    </Box>
  );
};
